// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web — components/BarraFiltros.tsx
//
//  Barra de filtros padrão das consultas: busca rápida ("Encontre rápido" do
//  Desktop) + filtros de dimensão/período (children) + "Limpar filtros" + ações
//  à direita (BotaoExportar, etc.).
//
//  A busca tem estado LOCAL e só avisa o pai depois do debounce: digitar não
//  dispara uma consulta por tecla nem perde o foco quando a lista recarrega.
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useDebounce } from "@/hooks/useDebounce";
import { cn } from "@/lib/utils";

interface CampoBuscaProps {
  /** Valor "oficial" (ex.: vindo da URL). */
  valor: string;
  /** Chamado com o texto já aparado, depois do debounce. */
  onBuscar: (texto: string) => void;
  placeholder?: string;
  debounceMs?: number;
  className?: string;
}

export function CampoBusca({ valor, onBuscar, placeholder = "Buscar...", debounceMs = 400, className }: CampoBuscaProps) {
  const [texto, setTexto] = useState(valor);
  const debounced = useDebounce(texto, debounceMs);
  const ultimoEnviado = useRef(valor);

  // Valor externo mudou (Limpar filtros, voltar no histórico) → reflete no campo.
  useEffect(() => {
    if (valor !== ultimoEnviado.current) {
      ultimoEnviado.current = valor;
      setTexto(valor);
    }
  }, [valor]);

  useEffect(() => {
    const t = debounced.trim();
    if (t !== ultimoEnviado.current) {
      ultimoEnviado.current = t;
      onBuscar(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  return (
    <div className={cn("relative w-full sm:w-80", className)}>
      <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
      <Input
        type="search"
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { ultimoEnviado.current = texto.trim(); onBuscar(texto.trim()); }
        }}
        placeholder={placeholder}
        aria-label={placeholder}
        className="h-9 pl-8"
      />
    </div>
  );
}

interface BarraFiltrosProps {
  /** Sem `onBuscar`, a barra não mostra o campo de busca. */
  busca?: string;
  onBuscar?: (texto: string) => void;
  placeholderBusca?: string;
  /** Quantos filtros estão fora do padrão (o `filtrosAtivos` do useFiltrosNaUrl). */
  filtrosAtivos?: number;
  onLimpar?: () => void;
  /** Filtros de dimensão e período. */
  children?: React.ReactNode;
  /** Ações à direita (exportar, etc.). */
  acoes?: React.ReactNode;
  className?: string;
}

export function BarraFiltros({
  busca = "", onBuscar, placeholderBusca, filtrosAtivos = 0, onLimpar, children, acoes, className,
}: BarraFiltrosProps) {
  return (
    <div className={cn("flex flex-wrap items-end gap-3", className)} role="search">
      {onBuscar && <CampoBusca valor={busca} onBuscar={onBuscar} placeholder={placeholderBusca} />}
      {children}
      {onLimpar && filtrosAtivos > 0 && (
        <Button type="button" variant="ghost" size="sm" className="h-9 gap-1" onClick={onLimpar}>
          <X className="h-4 w-4" />
          Limpar filtros{filtrosAtivos > 1 ? ` (${filtrosAtivos})` : ""}
        </Button>
      )}
      {acoes && <div className="ml-auto flex items-end gap-2">{acoes}</div>}
    </div>
  );
}
